import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { api } from '../api/client'
import type { Versorgungspunkt } from '../api/typen'
import AppRahmen from '../komponenten/AppRahmen'

/**
 * Schraenke und Sonderfaelle eines Versorgungspunkts mit Fortschritt je Eintrag. Sonderfaelle stehen
 * hier neben den Schraenken, weil sie wie diese zum Versorgungspunkt gehoeren (P7-04).
 */
export default function SchrankListe() {
  const { vpId } = useParams()
  const navigiere = useNavigate()
  const abfragen = useQueryClient()

  const loeschen = useMutation({
    mutationFn: () => api.loeschen(`/api/versorgungspunkte/${vpId}`),
    onSuccess: (_daten, _variablen) => navigiere(`/erfassungen/${punkt.data?.erfassungId ?? ''}`),
  })

  const punkt = useQuery({
    queryKey: ['versorgungspunkt', vpId],
    queryFn: () => api.get<Versorgungspunkt>(`/api/versorgungspunkte/${vpId}`),
  })

  const schraenke = punkt.data?.schraenke ?? []
  const sonderfaelle = punkt.data?.sonderfaelle ?? []

  return (
    <AppRahmen
      titel={punkt.data?.bezeichnung ?? 'Versorgungspunkt'}
      unter={punkt.data?.vpCode}
      zurueck={punkt.data ? `/erfassungen/${punkt.data.erfassungId}` : '/'}
      pfad={punkt.data?.pfad}
      ebene="versorgungspunkt"
      menue={[
        {
          beschriftung: 'Sonderfall erfassen',
          beiKlick: () => navigiere(`/versorgungspunkte/${vpId}/sonderfall-neu`),
        },
        {
          beschriftung: 'Versorgungspunkt umbenennen',
          eingabe: {
            feld: 'Bezeichnung',
            wert: punkt.data?.bezeichnung ?? '',
            hinweis: `Die Kennung ${punkt.data?.vpCode ?? ''} und die Ablage der Fotos bleiben unverändert.`,
            speichern: async (bezeichnung) => {
              const neu = await api.patch<Versorgungspunkt>(`/api/versorgungspunkte/${vpId}`, { bezeichnung })
              abfragen.setQueryData(['versorgungspunkt', vpId], neu)
              // Der Name steht auch in Pfadleisten und Listen anderer Bildschirme.
              void abfragen.invalidateQueries()
            },
          },
        },
        {
          beschriftung: 'Versorgungspunkt löschen',
          gefahr: true,
          bestaetigung: {
            frage: 'Alle Schränke, Sonderfälle, Fächer und Fotos dieses Versorgungspunkts werden entfernt.',
            bestaetigungstext: 'Endgültig löschen',
          },
          beiKlick: () => loeschen.mutate(),
        },
      ]}
      fuss={
        <button
          className="btn btn-primaer"
          onClick={() => navigiere(`/versorgungspunkte/${vpId}/schrank-neu`)}
        >
          Schrank hinzufügen
        </button>
      }
    >
      {punkt.isPending && <div className="spinner" />}

      {schraenke.length === 0 && <p className="sek">Noch kein Schrank angelegt.</p>}

      {schraenke.map((schrank) => {
        const anteil =
          schrank.anzahlFaecherSoll === 0 ? 0 : schrank.anzahlFaecherErfasst / schrank.anzahlFaecherSoll
        const zustand =
          schrank.status === 'vollstaendig'
            ? { klasse: 'st st-ok', text: 'vollständig' }
            : schrank.status === 'uebergangen'
              ? { klasse: 'st st-warn', text: 'bewusst übergangen' }
              : schrank.fehlendeFaecher > 0
                ? { klasse: 'st st-arbeit', text: `${schrank.fehlendeFaecher} offen` }
                : { klasse: 'st st-arbeit', text: 'in Arbeit' }

        return (
          <button key={schrank.id} className="karte" onClick={() => navigiere(`/schraenke/${schrank.id}`)}>
            {/* Das Gesamtfoto als Miniatur: vor Ort ist der Schrank am Bild schneller
                wiederzuerkennen als an Bezeichnung und Code (Rückmeldung Iteration 3). */}
            <div className="karte-oben">
              {schrank.gesamtfotoVorhanden ? (
                <img
                  className="miniatur"
                  src={`/api/schraenke/${schrank.id}/gesamtfoto`}
                  alt={`Gesamtfoto ${schrank.bezeichnung}`}
                />
              ) : (
                <div className="miniatur leer" aria-hidden="true" />
              )}
              <div className="karte-text">
                <div className="zeile">
                  <b>{schrank.bezeichnung}</b>
                  <span className={zustand.klasse}>{zustand.text}</span>
                </div>
                <span className="sek">
                  Fächer: {schrank.anzahlFaecherErfasst} aufgenommen, {schrank.anzahlFaecherSoll} konfiguriert
                  · {schrank.schrankCode}
                </span>
              </div>
            </div>
            <div className="fortschritt">
              <i style={{ width: `${Math.min(100, Math.round(anteil * 100))}%` }} />
            </div>
          </button>
        )
      })}

      {sonderfaelle.length > 0 && (
        <section className="gruppe">
          <h2 className="s">Sonderfälle</h2>
          {sonderfaelle.map((sonderfall) => {
            // Fortschritt wie bei den Schraenken: Gesamtfoto plus je Ebene ein Foto.
            const soll = sonderfall.anzahlEbenen + 1
            const ist = sonderfall.ebenenMitFoto + (sonderfall.gesamtfotoVorhanden ? 1 : 0)
            const vollstaendig = ist >= soll
            return (
              <button
                key={sonderfall.id}
                className="karte"
                onClick={() => navigiere(`/sonderfaelle/${sonderfall.id}`)}
              >
                <div className="karte-oben">
                  {sonderfall.gesamtfotoVorhanden ? (
                    <img
                      className="miniatur"
                      src={`/api/sonderfaelle/${sonderfall.id}/gesamtfoto`}
                      alt={`Gesamtfoto ${sonderfall.bezeichnung}`}
                    />
                  ) : (
                    <div className="miniatur leer" aria-hidden="true" />
                  )}
                  <div className="karte-text">
                    <div className="zeile">
                      <b>{sonderfall.bezeichnung}</b>
                      <span className={vollstaendig ? 'st st-ok' : 'st st-arbeit'}>
                        {vollstaendig ? 'vollständig' : `${soll - ist} offen`}
                      </span>
                    </div>
                    <span className="sek">
                      Ebenen: {sonderfall.ebenenMitFoto} von {sonderfall.anzahlEbenen} fotografiert ·{' '}
                      {sonderfall.sonderfallCode}
                    </span>
                  </div>
                </div>
                <div className="fortschritt">
                  <i style={{ width: `${Math.min(100, Math.round((ist / soll) * 100))}%` }} />
                </div>
              </button>
            )
          })}
        </section>
      )}
    </AppRahmen>
  )
}
